import React from 'react';
import {configData} from "../config.js";
import UseCaseDetails from "../components/useCase/UseCaseDetails.js";
import ReferenceListSelector from "../components/general/ReferenceListSelector.js";
import SelectedReferenceList from '../components/general/SelectedReferenceList.js';
import '../styles.scss';

import {fetchSigned} from "../shared/Utilities.js"

import {
  OwcButton, OwcTypography, OwcExpandableGroup, OwcExpandable,
   OwcProgressSpinner, OwcIcon,OwcInput,OwcAssistiveText
} from '@one/react';

/**
 * The interactive form for mapping a use case to entitlemnts
 *
 * @copyright Roche 2024
 * @author Nick Draper
 */
class MappingUseCase extends React.Component {
  UNSAVED_CHANGES_MESSAGE = "Unsaved changes, click Save Changes to save";
  /**
   * Constructor 
   * 
   * @param props The properties passed
   */
  constructor(props) {
    super(props);
    this.state = this.initialState();
    this.state.refDataUseList=[];
    if (props.useCaseId !== null)
    {
      this.state.isLoading = true;
    }
  }

  /** Runs whenever the properties of the control are changed
   * @param prevProps The previous properties dictionary
   * @param prevState The previous state dictionary
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.useCaseId !== this.props.useCaseId) {
      this.loadUseCaseData(this.props.useCaseId, false);
    }
  }

  /**
   * Runs once after construction after everything is initialised
   */
  componentDidMount() {
    // load the reference data and split out the affilate list
    fetchSigned(configData.REFDATA_API_URL + "?includeInactive=true")
      .then(res => res.json())
      .then(
        (result) => {
          const filteredDataUseList = result.filter(value =>
            value.type === "Data Licence Extract"
          );
          this.setState({
              refDataUseList: filteredDataUseList,
            },
            this.loadUseCaseData(this.props.useCaseId, false)
          );
        },
        // Note: it's important to handle errors here
        // instead of a catch() block so that we don't swallow
        // exceptions from actual bugs in components.
        (error) => {this.setState({error})}
      );
  }

  /**
   * Sets the initial state
   * @returns the initial state dictionary
   */
  initialState() {
    return ({
      isLoading: false,
      useCase:{useCaseId:-1, name:"", contactEmail:null,
        description: null,
        dataDescription: null,
        createdBy: null,
        dateCreated: null,
        dateUpdated: null,
        entitlementMappingComment: null,
      },
      dataUseList: [],
      submissionState: null,
      isDataUseExpanded: true,
      isUseCaseExpanded: true,
      isCommentsExpanded: true
    });
  }

  /**
   * Resets the form by setting the state back to the inital state
   */
  handleCancelClick() {
    //reset the state
    this.loadUseCaseData(this.props.useCaseId, true);
    this.props.onUnsavedChangesChange(false);
  }

  /**
   * Validates the form and submits it to the API if valid
   * @returns nothing
   */
  handleSubmitClick() {
    const submissionData = {...this.state.useCase}
    submissionData.dataUseList = [...this.state.dataUseList];
    console.log(submissionData)

    this.setState({ submissionState: "Saving ..." });

    const submitForm = () => {
      // decide if it is an update or insert and setup appropriately
      const url = `${configData.USECASE_API_URL}${submissionData.useCaseId}/`;
      const method = 'POST';
      return fetchSigned(url, {
        method: method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      })
    };

    submitForm().then((response) => {
      response.json().then((json) => {
        console.log(`API Response: ${JSON.stringify(json)}`);
        if (response.status === 201) // inserted successfully
        {
          // load the data back from the database to update all fields and ids
          this.loadUseCaseData(json["useCaseId"], false);
          this.props.onUnsavedChangesChange(false);
          this.setState({ submissionState: "Data Use Successfully Saved" });
        } else {
          if (json.errorText.startsWith("ERROR: duplicate key value violates unique constraint")) {
            this.setState({ submissionState: "Error: This data use has already been entered." });
          } else {
            this.setState({ submissionState: `Error saving data use ${json.errorText}` });
          }
        }
      });
    }).catch((error) => {
      this.setState({ submissionState: `Error saving data use ${error}` });
      console.error(`Error when saving data use: ${error}`);
    });

  };

  /**
   * Loads a useCase
   * @param {*} ev the event
   * @param {*} useCaseId  the selected agreeemntId
   */
  loadUseCaseData(useCaseId, clearSubmissionState = true) { 
    this.setState({isLoading:true});
    console.log("Selected use case id: " + useCaseId);

    if (useCaseId !== null) {
      fetchSigned(configData.USECASE_API_URL + useCaseId + "/")
        .then(res => res.json())
        .then(
          (result) => {
            // normalization for consistency
             if (result.entitlement_mapping_comment !== undefined) {
            result.entitlementMappingComment = result.entitlement_mapping_comment;
            delete result.entitlement_mapping_comment;
           }
            const newState = {useCase: result,
              dataUseList: result.dataUseList,
              isLoading: false
            }; 
            if (clearSubmissionState) {
              newState.submissionState = null;
            }
            this.setState(newState, this.props.onUnsavedChangesChange(false));
          },
          // Note: it's important to handle errors here
          // instead of a catch() block so that we don't swallow
          // exceptions from actual bugs in components.
          (error) => { this.setState({error}) }
      );
    } else {
      this.setState(this.initialState(), this.props.onUnsavedChangesChange(false));
    };    
  }

  handleListSelectionChange(selectedList, stateName) {  
    const newState = {submissionState: this.UNSAVED_CHANGES_MESSAGE};
    newState[stateName] = selectedList
    this.setState(newState, this.props.onUnsavedChangesChange(true));
  }

  renderCommentsSection() {
  const { useCase } = this.state;
  // Enabled if dataEntitlementsEnforced is true, read-only if false
  const editable = useCase.dataEntitlementsEnforced;

  return (
    <OwcExpandable
      variant="standard"
      round
      expanded={this.state.isCommentsExpanded}
      onExpandedChange={(ev) => this.setState({ isCommentsExpanded: ev.detail })}
    >
      <span slot="title">
        <OwcTypography variant="title5">
          {useCase.entitlementMappingComment && useCase.entitlementMappingComment.trim().length > 0
            ? "Comments - Entered"
            : "Comments - Not Entered"}
        </OwcTypography>
      </span>
      <span slot="content">
        <OwcInput
          placeholder="Enter comment for entitlement mapping..."
          key="CommentsInput"
          style={{ display: "inline-block" }}
          rows={useCase.entitlementMappingComment == null
            ? 4
            : Math.max(4, useCase.entitlementMappingComment.split(/\r\n|\r|\n/).length)
          }
          cols="100"
          label="Comments"
          value={useCase.entitlementMappingComment || ""}
          onValueChange={(ev) => {
            if (!editable) return; // Prevent editing if read-only
            const newUseCase = { ...useCase };
            newUseCase.entitlementMappingComment = ev.detail;
            this.setState(
              { useCase: newUseCase, submissionState: this.UNSAVED_CHANGES_MESSAGE },
              this.props.onUnsavedChangesChange(true)
            );
          }}
          type="textarea"
          no-clear-icon
          resizable="false"
          disabled={!editable} // Disabled if not editable
        >
          <OwcAssistiveText>Comments</OwcAssistiveText>
        </OwcInput>
      </span>
    </OwcExpandable>
  );
}


  renderRefDataPickerSection(SectionName, sectionTitle, listTitle, stateName, listStateName, disabled) {
    const sectionExpandedStateItem = `is${SectionName}Expanded`;
    const refListData = this.state[listStateName];
    const selectedItems = this.state[stateName];

    return(
      <OwcExpandable key={SectionName} 
            variant="standard" round
            expanded={this.state[sectionExpandedStateItem]}
            onExpandedChange={(ev) => {
              const newState = {};
              newState[sectionExpandedStateItem] = ev.detail;
              this.setState(newState)}
            } >
        <span key={SectionName + "title"} slot="title">
          <OwcTypography variant="title5">
            {`${sectionTitle} - ${this.state[stateName]?.length===0?"None Selected":`${this.state[stateName]?.length} Selected`}`}
          </OwcTypography>
        </span>
        <span key={SectionName + "content"} slot="content">
          {disabled
            ? <SelectedReferenceList selectedRefDataList={this.state.useCase.dataUseList} hideTitle />
            : <ReferenceListSelector size="small"
                title={listTitle}
                refListData={refListData}
                selectedItems={selectedItems}
                aggregateSublist
                onSelectedChange={(idList)=>this.handleListSelectionChange(idList, stateName)} />
          }
            
        </span>
      </OwcExpandable>
    );
  }

  /**
   * Renders the controls
   * @returns The JSX of the controls
   */
  render() {
    let messageColour = "black";
    if (this.state.submissionState !== null && this.state.submissionState.startsWith("Err")) {
      messageColour = "red";
    }
    const submissionState = this.state.submissionState;

    const mappingDisabled = (this.state.useCase.dataAccessApproved === false || this.state.useCase.dataEntitlementsEnforced === false)

    if (this.props.useCaseId === null) {
      return (<OwcTypography variant="title6" style={{marginLeft:"0.5em"}}>Save a data use in "Register" first</OwcTypography>);
    } else if (this.state.useCase.isGroup) {
      return (<OwcTypography variant="title6" style={{marginLeft:"0.5em"}}>Entitlement mapping is not available for groups</OwcTypography>)
    } else {
      if (this.state.isLoading){
        return (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
            <OwcProgressSpinner style={{marginTop:'30px'}}/>
          </div>);
      } else {
        return (
          <div>
            <OwcExpandableGroup multiple>
            <OwcExpandable variant="standard" round
                expanded={this.state.isUseCaseExpanded}
                onExpandedChange={(ev) => this.setState({ isUseCaseExpanded: ev.detail })} >
              <span slot="title">
                <div style={{display:'flex', flexDirection:'row', alignItems: 'center'}}>
                  <OwcTypography variant="title5">
                    {!this.state.isUseCaseExpanded? `Data Use ${this.state.useCase.useCaseId} - ${this.state.useCase.name}` : `Data Use ${this.state.useCase.useCaseId}`}
                  </OwcTypography>
                  {this.state.useCase.hiddenFromDataProducts
                    ? <OwcIcon key="useCaseHiddenIcon" id="useCaseHiddenIcon" name="element_hide"
                        style={{fontSize:"1.5em", paddingLeft:"0.5em"}} />
                    : ""
                  }
                </div>
              </span>
              <span slot="content">
                  <UseCaseDetails useCase={this.state.useCase}/>
                  <SelectedReferenceList selectedRefDataList={this.state.useCase.purposeList} title="Purpose Classification:"/>
                  {mappingDisabled === true?
                    <OwcTypography variant="title5" style={{marginLeft:"0.25em", color:"orange"}}>Entitlement mapping is disabled as 
                      {this.state.useCase.dataAccessApproved === false?" data access is denied ":" data entitlements are not enforced "}
                      in "Record Decisions"
                    </OwcTypography>
                  :
                  ""}
              </span>
            </OwcExpandable>
            {this.renderRefDataPickerSection("DataUse", "Data Licence Mapping", "", "dataUseList", "refDataUseList", mappingDisabled)}
             </OwcExpandableGroup>
             {this.renderCommentsSection()}
            <div style={{ display:"flex", flexDirection:"column" }}>
              <table width="100%">
                <tbody>
                  <tr>
                    <td style={{verticalAlign:"top"}} align="left">
                      <OwcButton elevated style={{ width: "fit-content" }}
                          onclick={() => this.handleCancelClick()}>
                        Clear Unsaved Changes
                      </OwcButton>
                    </td>

                    <td style={{verticalAlign:"top"}} align="right">
                      <OwcButton style={{ width: "fit-content" }}
                          disabled={((submissionState === null)
                                      || (submissionState === "Saving ...")
                                      || (submissionState.includes("Successfully"))) ? true : false}
                          onclick={() => this.handleSubmitClick()}>
                        {submissionState === "Saving ..." ? submissionState : "Save Changes"}
                      </OwcButton>
                    </td>
                  </tr>
                </tbody>
              </table>
              <OwcTypography variant="title6" style={{ marginBottom: 8, textAlign: "right", color: messageColour }}>
                {submissionState === "Saving ..." ? "" : submissionState}
              </OwcTypography>
            </div>
          </div>
        );
      }
    }
  }
}

export default MappingUseCase;
